import { useEffect, useRef } from "react"
import { Send } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { AiCreditUsageLink } from "@/components/shared/AiCreditUsageLink"
import { useDealAskRob } from "@/hooks/use-deal-ask-rob"
import { cn } from "@/lib/utils"

const ASK_ROB_PROMPTS = [
  "What should I verify next?",
  "What is the biggest risk here?",
  "What leverage might I have with this vendor?",
  "Is this ready for appraisal?",
  "What would you do next?",
]

const SUGGESTION_ACTION_LABELS = {
  activity_event: "Log Activity",
  deal_action: "Add to Priority Actions",
  follow_up: "Add to Priority Actions",
  note: "Add Note",
}

function suggestionActionLabel(suggestion) {
  return SUGGESTION_ACTION_LABELS[suggestion.suggestionType] ?? "Add"
}

export function DealAskRobPanel({
  askRob,
  showQuickPrompts = false,
  fullHeight = false,
  variant = "panel",
}) {
  function handleSubmit(event) {
    event.preventDefault()
    void askRob.submitMessage(askRob.input)
  }

  return (
    <div className={cn("flex min-h-0 flex-1 flex-col gap-3", fullHeight && "h-full")}>
      <AskRobMessages
        busySuggestionId={askRob.busySuggestionId}
        fullHeight={fullHeight}
        isLoading={askRob.isLoading}
        isSubmitting={askRob.isSubmitting}
        messages={askRob.messages}
        onAcceptSuggestion={askRob.acceptSuggestion}
        onRejectSuggestion={askRob.rejectSuggestion}
        suggestions={askRob.pendingSuggestions}
        variant={variant}
      />

      {askRob.error ? (
        <p className="rounded-lg border border-destructive/20 bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive">
          {askRob.error} <AiCreditUsageLink error={askRob.error} />
        </p>
      ) : null}

      <form className="relative mt-auto shrink-0" onSubmit={handleSubmit}>
        <Input
          aria-label="Ask Rob deal question"
          value={askRob.input}
          disabled={!askRob.canUsePersistedDealApi || askRob.isSubmitting}
          onChange={(event) => askRob.setInput(event.target.value)}
          placeholder="Ask Rob what to do next on this deal..."
          className="h-10 rounded-lg bg-background pr-10 text-xs shadow-sm placeholder:text-muted-foreground/50"
        />
        <Button
          type="submit"
          size="icon-xs"
          aria-label="Send Ask Rob question"
          disabled={!askRob.input.trim() || !askRob.canUsePersistedDealApi || askRob.isSubmitting}
          className="absolute right-1.5 top-1/2 -translate-y-1/2"
        >
          <Send className="h-3 w-3" />
        </Button>
      </form>

      {showQuickPrompts && askRob.messages.length === 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {ASK_ROB_PROMPTS.map((prompt) => (
            <Button
              key={prompt}
              type="button"
              variant="outline"
              size="xs"
              disabled={!askRob.canUsePersistedDealApi || askRob.isSubmitting}
              onClick={() => askRob.submitMessage(prompt)}
              className="h-auto justify-start rounded-md px-2 py-1.5 text-left text-[10px] font-medium normal-case tracking-normal"
            >
              {prompt}
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export function DealAskRobPanelForDeal({ deal, tab, showQuickPrompts = false, onSuggestionAccepted }) {
  const askRob = useDealAskRob({ deal, tab, onSuggestionAccepted })

  return <DealAskRobPanel askRob={askRob} showQuickPrompts={showQuickPrompts} />
}

function AskRobMessages({
  busySuggestionId,
  fullHeight,
  isLoading,
  isSubmitting,
  messages,
  onAcceptSuggestion,
  onRejectSuggestion,
  suggestions,
  variant,
}) {
  const scrollRef = useRef(null)
  const isDrawer = variant === "drawer"

  useEffect(() => {
    if (!scrollRef.current) return

    scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [isSubmitting, messages.length, suggestions.length])

  if (isLoading) {
    return <p className="text-xs text-muted-foreground">Loading Ask Rob history...</p>
  }

  if (messages.length === 0 && suggestions.length === 0 && !isSubmitting) {
    return null
  }

  return (
    <div
      aria-label="Ask Rob conversation"
      ref={scrollRef}
      role="region"
      className={cn(
        "space-y-2 overflow-y-auto rounded-lg border border-border p-2",
        isDrawer ? "bg-background" : "bg-muted/30",
        fullHeight ? "min-h-0 flex-1 basis-0" : "max-h-80",
      )}
    >
      {messages.map((message) => (
        <div
          key={message.id}
          className={cn(
            "rounded-lg border px-3 py-2 text-xs leading-relaxed",
            isDrawer && "max-w-[88%]",
            isDrawer && message.role === "assistant" && "mr-auto",
            isDrawer && message.role !== "assistant" && "ml-auto",
            message.role === "assistant"
              ? cn(
                  "text-foreground",
                  isDrawer ? "border-border bg-muted/40" : "border-primary/10 bg-primary/5",
                )
              : cn(
                  isDrawer ? "border-primary/20 bg-primary text-primary-foreground" : "border-border bg-background text-muted-foreground",
                ),
          )}
        >
          {isDrawer ? null : (
            <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              {message.role === "assistant" ? "Rob" : "You"}
            </p>
          )}
          <p>{message.body}</p>
        </div>
      ))}
      {isSubmitting ? (
        <div className="rounded-lg border border-border bg-muted/50 px-3 py-2 text-xs font-medium text-muted-foreground">
          Rob is thinking
          <span className="rob-thinking-dots" aria-hidden="true">
            <span>.</span>
            <span>.</span>
            <span>.</span>
          </span>
        </div>
      ) : null}
      <AskRobSuggestions
        busySuggestionId={busySuggestionId}
        onAccept={onAcceptSuggestion}
        onReject={onRejectSuggestion}
        suggestions={suggestions}
      />
    </div>
  )
}

function AskRobSuggestions({ suggestions, busySuggestionId, onAccept, onReject }) {
  if (suggestions.length === 0) return null

  return (
    <div className="space-y-2">
      {suggestions.map((suggestion) => {
        const isBusy = busySuggestionId === suggestion.id

        return (
          <div
            key={suggestion.id}
            className="rounded-lg border border-primary/20 bg-background p-3 text-xs shadow-sm"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-widest text-primary">
                  Suggested {suggestion.suggestionTypeLabel}
                </p>
                <p className="mt-1 font-bold text-foreground">{suggestion.title}</p>
                {suggestion.body ? (
                  <p className="mt-1 text-muted-foreground">{suggestion.body}</p>
                ) : null}
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <Button
                type="button"
                size="xs"
                disabled={Boolean(busySuggestionId)}
                onClick={() => onAccept(suggestion.id)}
              >
                {isBusy ? "Saving" : suggestionActionLabel(suggestion)}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="xs"
                disabled={Boolean(busySuggestionId)}
                onClick={() => onReject(suggestion.id)}
              >
                Dismiss
              </Button>
            </div>
          </div>
        )
      })}
    </div>
  )
}
